import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import brandConfig from '../config/brandConfig';

const navItems = [
  { path: '/', key: 'home' },
  { path: '/context', key: 'context' },
  { path: '/construction', key: 'construction' },
  { path: '/industries', key: 'industries' },
  { path: '/legislation', key: 'legislation' },
  { path: '/microfactory', key: 'microfactory' },
  { path: '/evolution', key: 'evolution' },
  { path: '/conclusion', key: 'conclusion' },
  { path: '/sources', key: 'sources' }
];

const Navbar = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200">
      <nav className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <div className="w-8 h-8 bg-navy flex items-center justify-center">
              <span className="text-white font-bold text-sm">{brandConfig.logoInitials}</span>
            </div>
            <span className="hidden sm:block text-sm font-semibold text-navy tracking-tight">
              {brandConfig.projectName.split(' ').slice(0, 2).join(' ')}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.slice(0, 5).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.key}`}
                className={`px-3 py-2 text-xs uppercase tracking-wider font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-[#0066CC]'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
            
            {/* Dropdown for remaining items */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                className="px-3 py-2 text-xs uppercase tracking-wider font-medium text-slate-600 hover:text-slate-900 flex items-center gap-1"
                data-testid="nav-more-dropdown"
              >
                {language === 'ro' ? 'Mai mult' : 'More'}
                <ChevronDown className="w-3 h-3" />
              </button>
              
              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full right-0 mt-1 bg-white border border-slate-200 shadow-lg min-w-[180px]"
                  >
                    {navItems.slice(5).map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        data-testid={`nav-dropdown-${item.key}`}
                        className={`block px-4 py-2 text-xs uppercase tracking-wider font-medium transition-colors ${
                          isActive(item.path)
                            ? 'text-[#0066CC] bg-slate-50'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                        }`}
                      >
                        {t(`nav.${item.key}`)}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border border-slate-300 hover:border-[#0066CC] hover:text-[#0066CC] transition-colors"
              data-testid="language-toggle"
            >
              <Globe className="w-3.5 h-3.5" />
              {language === 'ro' ? 'EN' : 'RO'}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-slate-200 py-4"
            >
              <div className="flex flex-col gap-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.key}`}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'text-[#0066CC] bg-slate-50'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-navy text-white py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-white/20 flex items-center justify-center">
              <span className="font-bold text-sm">{brandConfig.logoInitials}</span>
            </div>
            <div>
              <p className="text-sm font-semibold">{brandConfig.projectName}</p>
              <p className="confidential-badge mt-1 border-white/30 text-white/60">{t('footer.confidential')}</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-xs text-white/50 uppercase tracking-wider mb-1">{t('footer.contact')}</p>
            <p className="text-sm font-medium text-white mb-1">{brandConfig.contactName}</p>
            <a href={`tel:${brandConfig.contactPhone.replace(/\s/g, '')}`} className="text-sm text-white/70 hover:text-[#0066CC] transition-colors">
              {brandConfig.contactPhone}
            </a>
          </div>
          
          <div className="text-center md:text-right">
            <p className="text-xs text-white/60 mb-1">{t('footer.copyright')}</p>
            <p className="text-xs text-white/40">{t('footer.disclaimer')}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-16">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
